import { Event, ServerEvent, TServerSimpleCalendarEventList } from 'types/calendar';
import { RRule, datetime } from 'rrule';
import dayjs from 'dayjs';
import { DEFAULT_CALENDAR_COLOR } from 'constants/common';

type RefillTaskItem = {
    patientName: string;
    description: string;
    submittedAt?: string;
    submittedOn: string;
    lastUpdatedAt?: string;
    lastUpdatedOn?: string;
    isRecurring?: boolean;
    recurrenceId?: number | null;
    canModifyRecurrence?: boolean;
    hasOutstandingBalance?: boolean;
    clientId?: number;
    textVisitId?: number;
    textVisitUuid?: string;
    providerTreatmentSubmitted?: boolean;
    startAt?: string;
    specialty?: string;
};

export const transferGoogleEventToServerEvent = (data: Event): ServerEvent => {
    return {
        start: data.start.dateTime,
        end: data.end.dateTime,
        eventId: data.id,
        colorId: data.channelId,
        description: data.description,
        email: data.attendees?.map((item) => item.email),
        location: data.location,
        summary: data.summary,
        timeZone: data.start.timeZone,
        providerId: data.providerId,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        sendForm: data.sendForm,
    };
};

export const transferServerEventToGoogleEvent = (data: ServerEvent): Event => {
    return {
        id: data.eventId,
        summary: data.summary,
        start: {
            timeZone: data.timeZone,
            dateTime: data.start,
        },
        end: {
            timeZone: data.timeZone,
            dateTime: data.end,
        },
        attendees: data.email?.map((email) => {
            return {
                email,
                displayName: email,
            };
        }),
        providerId: data.providerId,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        sendForm: data.sendForm,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        location: data.location,
        colorId: data.colorId,
        description: data.description,
    };
};

export const transferServiceSimpleListToGoogleEvent = (
    data: TServerSimpleCalendarEventList,
    start: string,
    end: string,
    providerId?: number,
): Event[] => {
    const ret: Event[] = [];
    data?.appointmentList?.forEach((item) => {
        const event: Event = {
            id: `${item.id}`,
            summary: item.title,
            start: {
                timeZone: 'UTC',
                dateTime: item.startAt,
            },
            end: {
                timeZone: 'UTC',
                dateTime: item.endAt,
            },
            attendees: [],
            description: '',
            type: 'appointment',
            appointmentStatus: item.appointmentStatus || undefined,
            color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
            scheduledBy: item.scheduledBy,
            serviceSpecialty: item.serviceSpecialty,
            clientId: item.clientId,
            isSample: item.isSample,
            timezone: item.timezone,
            providerId: item.providerId,
        };
        ret.push(event);
    });
    data?.googleList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: `${item.id}`,
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'google',
                appointmentStatus: item.appointmentStatus || undefined,
                color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
                timezone: item.timezone,
                providerId: item.providerId,
            };
            ret.push(event);
        } else {
            const startDay = new Date(start);
            let endDay = new Date(end);
            if (item.recurrenceRule && item.recurrenceRule.includes('UNTIL=')) {
                try {
                    const endStrfull = item.recurrenceRule.split('UNTIL=')[1];
                    if (endStrfull.includes(';')) {
                        //"RRULE:FREQ=WEEKLY;WKST=SU;UNTIL=20260101T065959Z;INTERVAL=2;BYDAY=TU"
                        const endStr = endStrfull.split(';')[0];
                        endDay = dayjs(
                            endStr?.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6Z'),
                        ).toDate();
                        if (Number.isNaN(endDay.getTime())) {
                            console.log('Invalid Date');
                            return;
                        }
                    }
                } catch (e) {
                    return;
                }
            }
            let ruleString = item.recurrenceRule;
            let exdateString: string | undefined = '';
            let dtstartString: string | undefined = '';
            let exdate: dayjs.Dayjs | undefined;
            if (ruleString.includes('\n')) {
                const ruleArr = ruleString.split('\n');
                exdateString = ruleArr.find((line) => line.startsWith('EXDATE'));
                dtstartString = ruleArr.find((line) => line.startsWith('DTSTART'));
                for (let i = 0; i < ruleArr.length; i++) {
                    const ruleItem = ruleArr[i];
                    if (ruleItem.startsWith('RRULE:')) {
                        ruleString = ruleItem;
                        break;
                    }
                }
            }
            if (!ruleString.startsWith('RRULE:')) {
                return;
            }
            if (ruleString.includes('FREQ=YEARLY')) {
                const event: Event = {
                    id: `${item.id}`,
                    summary: item.title,
                    start: {
                        timeZone: 'UTC',
                        dateTime: dayjs(item.startAt).year(dayjs().year()).toISOString(),
                    },
                    end: {
                        timeZone: 'UTC',
                        dateTime: dayjs(item.endAt).year(dayjs().year()).toISOString(),
                    },
                    attendees: [],
                    description: '',
                    type: 'google',
                    appointmentStatus: item.appointmentStatus || undefined,
                    color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                    scheduledBy: item.scheduledBy,
                    serviceSpecialty: item.serviceSpecialty,
                    timezone: item.timezone,
                    providerId: item.providerId,
                };
                ret.push(event);
                return;
            }

            const parsed = RRule.fromString(ruleString);
            let dtstart = new Date(item.startAt);
            if (exdateString) {
                const exdateStringArr = exdateString.substring(exdateString.indexOf('=') + 1).split(':');
                if (exdateStringArr && exdateStringArr.length === 2) {
                    try {
                        exdate = dayjs.tz(exdateStringArr[1], exdateStringArr[0]);
                    } catch (e) {
                        console.error('Error parsing EXDATE:', e);
                    }
                }
            }
            if (dtstartString) {
                const dtstartStringArr = dtstartString.substring(dtstartString.indexOf('=') + 1).split(':');
                if (dtstartStringArr && dtstartStringArr.length === 2) {
                    dtstart = dayjs.tz(dtstartStringArr[1], dtstartStringArr[0]).toDate();
                }
            }
            const rule = new RRule({
                ...parsed.origOptions,
                dtstart,
            });

            const dates = rule.between(
                datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()),
                datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()),
            );

            dates.forEach((date) => {
                const duration = new Date(item.endAt).getTime() - new Date(item.startAt).getTime();
                let startTime = dayjs(date)
                    .hour(dayjs(item.startAt).hour())
                    .minute(dayjs(item.startAt).minute())
                    .second(0);
                if (exdate) {
                    if (exdate.format('YYYY-MM-DD') === startTime.format('YYYY-MM-DD')) {
                        return;
                    }
                }
                // wait confirm
                if (item.timezone) {
                    const utcDay = dayjs(item.startAt).utc().date();
                    const localDay = dayjs(item.startAt).tz(item.timezone).date();
                    if (utcDay > localDay) {
                        startTime = startTime.add(1, 'day');
                    } else if (utcDay < localDay) {
                        startTime = startTime.add(-1, 'day');
                    }
                }

                const endTime = dayjs(startTime).add(duration, 'ms');
                const event: Event = {
                    id: `${item.id}`,
                    summary: item.title,
                    start: {
                        timeZone: 'UTC',
                        dateTime: startTime.toISOString(),
                    },
                    end: {
                        timeZone: 'UTC',
                        dateTime: endTime.toISOString(),
                    },
                    attendees: [],
                    description: '',
                    type: 'google',
                    appointmentStatus: item.appointmentStatus || undefined,
                    color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                    scheduledBy: item.scheduledBy,
                    serviceSpecialty: item.serviceSpecialty,
                    timezone: item.timezone,
                    providerId: item.providerId,
                };
                ret.push(event);
            });
        }
    });

    data?.blockerList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: `${item.id}`,
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'blocker',
                appointmentStatus: item.appointmentStatus || undefined,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
                isSample: item.isSample,
                timezone: item.timezone,
                providerId: item.providerId,
            };
            ret.push(event);
        } else if (item.recurrenceRule && !item.recurrenceRule.includes('UNTIL=')) {
            const startDay = new Date(start);
            const endDay = new Date(end);

            // const startDay = new Date("2025-03-29");
            // const endDay = new Date("2025-04-12")
            // const rule = RRule.fromString('RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR');
            // rule.options.dtstart = new Date(item.startAt);
            // const dates = rule.between(datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()), datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()))

            const parsed = RRule.fromString(item.recurrenceRule);
            const dtstart = new Date(item.startAt);
            const rule = new RRule({
                ...parsed.origOptions,
                dtstart,
            });
            const dates = rule.between(
                datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()),
                datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()),
            );

            dates.forEach((date) => {
                const duration = new Date(item.endAt).getTime() - new Date(item.startAt).getTime();
                const startTime = dayjs(date)
                    .hour(dayjs(item.startAt).hour())
                    .minute(dayjs(item.startAt).minute())
                    .second(0);
                //todo if in diff timezone, dates will be diff
                // const timezone = 'America/New_York';
                // const startTimeInTimezone = startTime.tz(timezone);

                // // 提取日期部分进行比较
                // const dateOnly1 = dayjs(startTime.format('YYYY-MM-DD HH:mm:ss')).hour(0).minute(0).second(0);
                // const dateOnly2 = dayjs(startTimeInTimezone.format('YYYY-MM-DD HH:mm:ss')).hour(0).minute(0).second(0);

                // const differenceInSecond = dateOnly1.diff(dateOnly2, 'seconds');
                // let differenceInDays = 0;
                // if (differenceInSecond === 86400) {
                //     differenceInDays = 1;
                //     startTime.add(-1, 'day');
                // } else if (differenceInSecond === -86400) {
                //     differenceInDays = -1;
                //     startTime.add(1, 'day');
                // }

                const endTime = dayjs(startTime).add(duration, 'ms');
                const event: Event = {
                    id: `${item.id}`,
                    summary: item.title,
                    start: {
                        timeZone: 'UTC',
                        dateTime: startTime.toISOString(),
                    },
                    end: {
                        timeZone: 'UTC',
                        dateTime: endTime.toISOString(),
                    },
                    attendees: [],
                    description: '',
                    type: 'blocker',
                    appointmentStatus: item.appointmentStatus || undefined,
                    scheduledBy: item.scheduledBy,
                    serviceSpecialty: item.serviceSpecialty,
                    timezone: item.timezone,
                    providerId: item.providerId,
                };
                ret.push(event);
            });
        }
    });

    // Text visits are task-like items (shown as a pill such as "2 text visits").
    // Backend returns a flat list; for calendar UI we aggregate by date.
    const textVisits = data?.textVisitList || [];
    if (textVisits.length > 0) {
        const byDayTextVisits: Record<string, number> = {};
        const byDayTextVisitItems: Record<string, RefillTaskItem[]> = {};
        const byDayPrescriptionRefills: Record<string, number> = {};
        const byDayPrescriptionRefillItems: Record<string, RefillTaskItem[]> = {};

        const todayKey = dayjs().format('YYYY-MM-DD');

        for (const tv of textVisits) {
            // Use startAt as the primary date key so recurring occurrences appear on their
            // correct date (e.g. a recurrence with startAt=Apr 13 shows on Apr 13, not
            // the original patientSubmitAt date). Fall back to other timestamps if absent.
            const ts = tv.snoozeUntil || tv.startAt || tv.patientSubmitAt || tv.updatedAt || tv.assignedProviderAt;
            if (!ts) continue;
            // Backend stores UTC (with or without Z). Force UTC parse, then convert to local
            // for the day key so it lines up with Week/Month/Day cells (which use local format).
            const rawDayKey = dayjs.utc(ts).local().format('YYYY-MM-DD');
            // If the task date is in the past, pin it to today so it stays visible
            // in the current calendar view (unresolved past tasks surface to today).
            const dayKey = rawDayKey < todayKey ? todayKey : rawDayKey;

            if (tv.serviceType === 'PRESCRIPTION_REFILL') {
                byDayPrescriptionRefills[dayKey] = (byDayPrescriptionRefills[dayKey] || 0) + 1;

                const submittedAt = tv.patientSubmitAt || tv.assignedProviderAt || tv.updatedAt;
                const { updatedAt } = tv;
                const patientName = [tv.patientFirstName, tv.patientLastName].filter(Boolean).join(' ') || 'Unknown';
                const stateTag = tv.state ? `[${tv.state}]` : '';
                const specialty = tv.specialty || tv.serviceType || 'Prescription refill';
                const description = `${stateTag} ${specialty}: Prescription refill`.trim();

                const item: RefillTaskItem = {
                    patientName,
                    description,
                    submittedAt: submittedAt || undefined,
                    submittedOn: submittedAt ? dayjs(submittedAt).utc().format('MMM D') : '',
                    lastUpdatedAt: updatedAt || undefined,
                    lastUpdatedOn: updatedAt ? dayjs(updatedAt).utc().format('MMM D') : undefined,
                    isRecurring: !!(tv.isRecurring || tv.recurrenceId),
                    recurrenceId: tv.recurrenceId,
                    canModifyRecurrence: !!(tv as any).canModifyRecurrence,
                    hasOutstandingBalance: !!(tv.hasOutstandingBalance || tv.hasUnpaidOrFailedStatus),
                    clientId: tv.clientId,
                    textVisitId: tv.id,
                    textVisitUuid:
                        tv.textVisitUuid ||
                        // fallback for older payloads
                        (tv as any).uuid ||
                        (tv as any).caseUuid ||
                        (tv as any).text_visit_uuid,
                    providerTreatmentSubmitted: tv.providerTreatmentSubmitted,
                    startAt: tv.startAt,
                    specialty: tv.specialty,
                };

                if (!byDayPrescriptionRefillItems[dayKey]) byDayPrescriptionRefillItems[dayKey] = [];
                byDayPrescriptionRefillItems[dayKey].push(item);
            } else {
                byDayTextVisits[dayKey] = (byDayTextVisits[dayKey] || 0) + 1;

                const submittedAt = tv.patientSubmitAt || tv.assignedProviderAt || tv.updatedAt;
                const { updatedAt } = tv;
                const patientName = [tv.patientFirstName, tv.patientLastName].filter(Boolean).join(' ') || 'Unknown';
                const stateTag = tv.state ? `[${tv.state}]` : '';
                const specialty = tv.specialty || tv.serviceType || 'Text visit';
                const description = `${stateTag} ${specialty}`.trim();

                const tvItem: RefillTaskItem = {
                    patientName,
                    description,
                    submittedAt: submittedAt || undefined,
                    submittedOn: submittedAt ? dayjs(submittedAt).format('MMM D') : '',
                    lastUpdatedAt: updatedAt || undefined,
                    lastUpdatedOn: updatedAt ? dayjs(updatedAt).format('MMM D') : undefined,
                    isRecurring: !!(tv.isRecurring || tv.recurrenceId),
                    recurrenceId: tv.recurrenceId,
                    canModifyRecurrence: !!(tv as any).canModifyRecurrence,
                    hasOutstandingBalance: !!(tv.hasOutstandingBalance || tv.hasUnpaidOrFailedStatus),
                    clientId: tv.clientId,
                    textVisitId: tv.id,
                    textVisitUuid:
                        tv.textVisitUuid || (tv as any).uuid || (tv as any).caseUuid || (tv as any).text_visit_uuid,
                    providerTreatmentSubmitted: tv.providerTreatmentSubmitted,
                    startAt: tv.startAt,
                    specialty: tv.specialty,
                };

                if (!byDayTextVisitItems[dayKey]) byDayTextVisitItems[dayKey] = [];
                byDayTextVisitItems[dayKey].push(tvItem);
            }
        }

        const pushTaskPill = (
            dayKey: string,
            idPrefix: string,
            summary: string,
            extra?: {
                prescriptionRefillItems?: RefillTaskItem[];
                textVisitItems?: RefillTaskItem[];
            },
        ) => {
            // Use local midnight so the ISO string stays consistent with the local dayKey.
            const startAt = dayjs(`${dayKey}T00:00:00`);
            ret.push({
                id: `${idPrefix}_${dayKey}`,
                summary,
                description: '',
                start: { timeZone: 'UTC', dateTime: startAt.toISOString() },
                end: { timeZone: 'UTC', dateTime: startAt.add(10, 'minute').toISOString() },
                attendees: [],
                type: 'task',
                color: DEFAULT_CALENDAR_COLOR,
                // Keep a stable day key so calendar views can place pills correctly
                // regardless of browser/selected timezone conversions.
                taskDayKey: dayKey,
                ...extra,
            });
        };

        Object.entries(byDayPrescriptionRefills).forEach(([dayKey, count]) => {
            pushTaskPill(dayKey, 'prescription_refills', `📃 ${count} Prescription refills`, {
                prescriptionRefillItems: byDayPrescriptionRefillItems[dayKey] || [],
            });
        });
        Object.entries(byDayTextVisits).forEach(([dayKey, count]) => {
            pushTaskPill(dayKey, 'text_visits', `✏️ ${count} text visits`, {
                textVisitItems: byDayTextVisitItems[dayKey] || [],
            });
        });
    }
    if (providerId !== undefined) {
        ret.forEach((e) => {
            if (!e.providerId) {
                e.providerId = providerId;
            }
        });
    }
    return ret;
};

export const EventData = {
    transferGoogleEventToServerEvent,
    transferServerEventToGoogleEvent,
    transferServiceSimpleListToGoogleEvent,
};
